import { ItemType } from 'antd/es/menu/hooks/useItems';
import { ScenarioAttributesDto, ScenarioListRequestDto, ScenarioTvMatchingKind, TimePeriod } from '../../../model/CORE';
import { ModalHandler } from '../../panels/ModalHandler';
import AppContext from '../../../common/context/AppContext';
import { ServiceFactory } from '../../../service/ServiceFactory';
import { notification } from 'antd';
import { ScenarioUtils } from '../../panels/scenario/ScenarioUtils';
import { ScenarioAttributesColumn } from '../../table/ScenarioAttributesTable';

export interface ScenarioMenuState {
    isApplicableScenarioLoading: boolean;
    requestedScenarioTp?: TimePeriod;
    applicableScenarios?: Array<ScenarioAttributesDto>;
}

export class ApplicableScenarioMenuUtils {
    public static applicableScenarioMenu<U extends ScenarioMenuState>(
        concernedTvIds: string[],
        applicability: TimePeriod,
        state: U,
        setState: (state: U) => void,
    ): ItemType {
        if (concernedTvIds.length === 0) {
            return null;
        }
        const applicableScenarios: Array<ItemType> = this.getApplicableScenariosMenuItems(
            concernedTvIds,
            applicability,
            state.isApplicableScenarioLoading,
            state.applicableScenarios,
        );
        return {
            key: `scenario-sub-menu-${concernedTvIds.join()}`,
            label: 'Applicable Scenarios',
            onMouseEnter: () =>
                ApplicableScenarioMenuUtils.handleScenariosRequest(concernedTvIds, applicability, state, setState),
            children: applicableScenarios,
        };
    }

    private static getApplicableScenariosMenuItems(
        tvIds: string[],
        applicability: TimePeriod,
        isApplicableScenarioLoading: boolean,
        applicableScenarios: Array<ScenarioAttributesDto>,
    ): Array<ItemType> {
        const keyId = tvIds.join();
        if (isApplicableScenarioLoading || !applicableScenarios) {
            return [
                {
                    key: 'AS-section-loading-' + keyId,
                    label: 'Loading ...',
                    disabled: true,
                    style: { background: 'rgb(58, 58, 58)', fontWeight: 'bold' },
                },
            ];
        } else {
            const onSelectScenario = (scenario: ScenarioAttributesDto) =>
                ModalHandler.showApplicableScenariosByTV(tvIds, applicability, scenario.scenarioId);
            const scenariosMenu: Array<ItemType> = [];
            if (
                applicableScenarios.find(
                    value =>
                        ScenarioAttributesColumn.getMatchingKind(value) ===
                        ScenarioAttributesColumn.getMatchingKindLabel(ScenarioTvMatchingKind.SAME_TRAFFIC_VOLUME),
                )
            ) {
                scenariosMenu.push({
                    key: 'AS-section-STV-' + keyId,
                    label: 'Same traffic volume',
                    disabled: true,
                    style: { background: 'rgb(58, 58, 58)', fontWeight: 'bold' },
                });
                applicableScenarios
                    .filter(
                        value =>
                            ScenarioAttributesColumn.getMatchingKind(value) ===
                            ScenarioAttributesColumn.getMatchingKindLabel(ScenarioTvMatchingKind.SAME_TRAFFIC_VOLUME),
                    )
                    .forEach((scenario, idx) => {
                        scenariosMenu.push({
                            key: 'AS-section-STV-' + keyId + '-' + idx,
                            label:
                                scenario.scenarioId +
                                ' (' +
                                (scenario.problematicCapturedFlights ? scenario.problematicCapturedFlights.length : 0) +
                                ' flights' +
                                (tvIds.length > 1
                                    ? ' | match: ' +
                                      Object.keys(scenario.scenarioTvCaptureDetailsByTv ?? {}).length +
                                      ' tv(s)'
                                    : '') +
                                ')',
                            onClick: () => onSelectScenario(scenario),
                        });
                    });
            }
            if (
                applicableScenarios.find(
                    value =>
                        ScenarioAttributesColumn.getMatchingKind(value) ===
                        ScenarioAttributesColumn.getMatchingKindLabel(ScenarioTvMatchingKind.SAME_REFERENCE_LOCATION),
                )
            ) {
                scenariosMenu.push({
                    key: 'AS-section-SRL-' + keyId,
                    label: 'Same reference location',
                    disabled: true,
                    style: { background: 'rgb(58, 58, 58)', fontWeight: 'bold' },
                });
                applicableScenarios
                    .filter(
                        value =>
                            ScenarioAttributesColumn.getMatchingKind(value) ===
                            ScenarioAttributesColumn.getMatchingKindLabel(
                                ScenarioTvMatchingKind.SAME_REFERENCE_LOCATION,
                            ),
                    )
                    .forEach((scenario, idx) => {
                        scenariosMenu.push({
                            key: 'AS-section-SRL-' + keyId + '-' + idx,
                            label:
                                scenario.scenarioId +
                                ' (' +
                                (scenario.problematicCapturedFlights ? scenario.problematicCapturedFlights.length : 0) +
                                ' flights' +
                                (tvIds.length > 1
                                    ? ' | match: ' +
                                      Object.keys(scenario.scenarioTvCaptureDetailsByTv ?? {}).length +
                                      ' tv(s)'
                                    : '') +
                                ')',
                            onClick: () => onSelectScenario(scenario),
                        });
                    });
            }
            if (
                applicableScenarios.find(
                    value =>
                        ScenarioAttributesColumn.getMatchingKind(value) ===
                        ScenarioAttributesColumn.getMatchingKindLabel(
                            ScenarioTvMatchingKind.OVERLAPPING_REFERENCE_LOCATION,
                        ),
                )
            ) {
                scenariosMenu.push({
                    key: 'AS-section-ORL-' + keyId,
                    label: 'Overlapping reference location',
                    disabled: true,
                    style: { background: 'rgb(58, 58, 58)', fontWeight: 'bold' },
                });
                applicableScenarios
                    .filter(
                        value =>
                            ScenarioAttributesColumn.getMatchingKind(value) ===
                            ScenarioAttributesColumn.getMatchingKindLabel(
                                ScenarioTvMatchingKind.OVERLAPPING_REFERENCE_LOCATION,
                            ),
                    )
                    .forEach((scenario, idx) => {
                        scenariosMenu.push({
                            key: 'AS-section-ORL-' + keyId + '-' + idx,
                            label:
                                scenario.scenarioId +
                                ' (' +
                                (scenario.problematicCapturedFlights ? scenario.problematicCapturedFlights.length : 0) +
                                ' flights' +
                                (tvIds.length > 1
                                    ? ' | match: ' +
                                      Object.keys(scenario.scenarioTvCaptureDetailsByTv ?? {}).length +
                                      ' tv(s)'
                                    : '') +
                                ')',
                            onClick: () => onSelectScenario(scenario),
                        });
                    });
            }
            if (
                applicableScenarios.find(
                    value =>
                        ScenarioAttributesColumn.getMatchingKind(value) ===
                        ScenarioAttributesColumn.getMatchingKindLabel(ScenarioTvMatchingKind.INDIRECT_OFFLOAD),
                )
            ) {
                scenariosMenu.push({
                    key: 'AS-section-IO-' + keyId,
                    label: 'indirect offload',
                    disabled: true,
                    style: { background: 'rgb(58, 58, 58)', fontWeight: 'bold' },
                });
                applicableScenarios
                    .filter(
                        value =>
                            ScenarioAttributesColumn.getMatchingKind(value) ===
                            ScenarioAttributesColumn.getMatchingKindLabel(ScenarioTvMatchingKind.INDIRECT_OFFLOAD),
                    )
                    .forEach((scenario, idx) => {
                        scenariosMenu.push({
                            key: 'AS-section-IO-' + keyId + '-' + idx,
                            label:
                                scenario.scenarioId +
                                ' (' +
                                (scenario.problematicCapturedFlights ? scenario.problematicCapturedFlights.length : 0) +
                                ' flights' +
                                (tvIds.length > 1
                                    ? ' | match: ' +
                                      Object.keys(scenario.scenarioTvCaptureDetailsByTv ?? {}).length +
                                      ' tv(s)'
                                    : '') +
                                ')',
                            onClick: () => onSelectScenario(scenario),
                        });
                    });
            }
            if (
                applicableScenarios.find(
                    value =>
                        ScenarioAttributesColumn.getMatchingKind(value) ===
                        ScenarioAttributesColumn.MULTIPLE_TV_MATCHING_LABEL,
                )
            ) {
                scenariosMenu.push({
                    key: 'AS-section-MUL-' + keyId,
                    label: 'Multiple',
                    disabled: true,
                    style: { background: 'rgb(58, 58, 58)', fontWeight: 'bold' },
                });
                applicableScenarios
                    .filter(
                        value =>
                            ScenarioAttributesColumn.getMatchingKind(value) ===
                            ScenarioAttributesColumn.MULTIPLE_TV_MATCHING_LABEL,
                    )
                    .forEach((scenario, idx) => {
                        scenariosMenu.push({
                            key: 'AS-section-MUL-' + keyId + '-' + idx,
                            label:
                                scenario.scenarioId +
                                ' (' +
                                (scenario.problematicCapturedFlights ? scenario.problematicCapturedFlights.length : 0) +
                                ' flights' +
                                (tvIds.length > 1
                                    ? ' | match: ' +
                                      Object.keys(scenario.scenarioTvCaptureDetailsByTv ?? {}).length +
                                      ' tv(s)'
                                    : '') +
                                ')',
                            onClick: () => onSelectScenario(scenario),
                        });
                    });
            }
            if (scenariosMenu.length === 0) {
                return [
                    {
                        key: 'AS-section-loading-' + keyId,
                        label: 'No Applicable Scenario Found',
                        disabled: true,
                        style: { background: 'rgb(58, 58, 58)', fontWeight: 'bold' },
                    },
                ];
            }
            return scenariosMenu;
        }
    }

    private static handleScenariosRequest = (
        tvIds: string[],
        applicability: TimePeriod,
        state: ScenarioMenuState,
        setState: (state: ScenarioMenuState) => void,
    ) => {
        const scenarioPeriod = applicability ? applicability : ScenarioUtils.getDefaultScenarioTimePeriod();
        if (
            !state.isApplicableScenarioLoading &&
            (!state.requestedScenarioTp ||
                state.requestedScenarioTp.from.getTime() !== scenarioPeriod.from.getTime() ||
                state.requestedScenarioTp.to.getTime() !== scenarioPeriod.to.getTime())
        ) {
            setState({ isApplicableScenarioLoading: true, requestedScenarioTp: scenarioPeriod });
            const req: ScenarioListRequestDto = {
                dataset: AppContext.getDataset(),
                timePeriod: scenarioPeriod,
                tvIds: tvIds,
                includeCapturedCount: false,
            };
            ServiceFactory.stamCoreService()
                .getScenarioList(req)
                .then(reply => {
                    if (reply.error) {
                        throw new Error(reply.error.message);
                    }
                    setState({
                        applicableScenarios: reply.data,
                        isApplicableScenarioLoading: false,
                        requestedScenarioTp: scenarioPeriod,
                    });
                })
                .catch(reason => {
                    notification.error({ message: 'Error', description: reason.message, duration: 0 });
                });
        }
    };
}
